import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { ChatSession, UserQuota } from '../types';

export interface QuotaErrorEvent {
  code?: string;
  message?: string;
}

export interface QuotaBanner {
  level: 'near_limit' | 'limit_reached' | string;
  message: string;
  action?: {
    label: string;
    onClick: () => void;
  };
}

interface UseQuotaBannerInput {
  sessionLimitWarning?: ChatSession['limitWarning'];
  isAnonymous: boolean;
  quota?: UserQuota | null;
  fallbackEnabled?: boolean;
  quotaErrorEvent?: QuotaErrorEvent | null;
}

export const useQuotaBanner = ({
  sessionLimitWarning,
  isAnonymous,
  quota,
  fallbackEnabled = false,
  quotaErrorEvent,
}: UseQuotaBannerInput): QuotaBanner | null => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return useMemo(() => {
    if (sessionLimitWarning) {
      return sessionLimitWarning;
    }

    if (isAnonymous) {
      return null;
    }

    const packageStatus = quota?.packageStatus ?? 'available';
    const hasAdvanceBalance = quota?.hasAdvanceBalance ?? false;
    const canSend = quota?.canSend ?? true;
    const openSettings = () => {
      window.dispatchEvent(new CustomEvent('settings:open'));
    };
    const openSubscription = () => {
      navigate('/subscription');
    };

    if (quotaErrorEvent || (quota && !canSend)) {
      if (!fallbackEnabled && hasAdvanceBalance) {
        return {
          level: 'limit_reached',
          message: t('chat.warnings.packageQuotaExceededHasAdvance'),
          action: {
            label: t('chat.warnings.openSettings'),
            onClick: openSettings,
          },
        };
      }

      return {
        level: 'limit_reached',
        message: t('chat.warnings.quotaExceeded'),
        action: {
          label: t('chat.warnings.upgradeOrBuy'),
          onClick: openSubscription,
        },
      };
    }

    if (packageStatus !== 'low') {
      return null;
    }

    if (!fallbackEnabled && hasAdvanceBalance) {
      return {
        level: 'near_limit',
        message: t('chat.warnings.nearLimitHasAdvance'),
        action: {
          label: t('chat.warnings.openSettings'),
          onClick: openSettings,
        },
      };
    }

    if (!fallbackEnabled && !hasAdvanceBalance) {
      return {
        level: 'near_limit',
        message: t('chat.warnings.nearLimitNoAdvance'),
        action: {
          label: t('chat.warnings.buyTokens'),
          onClick: openSubscription,
        },
      };
    }

    if (fallbackEnabled && !hasAdvanceBalance) {
      return {
        level: 'near_limit',
        message: t('chat.warnings.lowAdvanceTokens'),
        action: {
          label: t('chat.warnings.buyTokens'),
          onClick: openSubscription,
        },
      };
    }

    return null;
  }, [fallbackEnabled, isAnonymous, navigate, quota, quotaErrorEvent, sessionLimitWarning, t]);
};
